import styled, { css, useTheme } from "styled-components";
import * as AiIcons from "react-icons/ai";
import { useState, useEffect, useRef, cloneElement, useContext } from "react";
import { CSSTransition } from "react-transition-group";
import { StyledHoverMenu } from "../HoverMenu/HoverMenu";
import Icon from "../Icon/Icon";
import { AlertContext } from "../../App";
import { defaultBoxShadow } from "../../assetsConfig";

const StyledInfoPopup = styled.div<{ type: "error" | "success" | "alert" }>`
	position: fixed;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	min-width: 300px;
	bottom: 2rem;
	right: 0;
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	z-index: 999999999999999999;
	box-shadow: ${defaultBoxShadow};
	padding: 1rem;

	&.alert-enter {
		opacity: 0;
		right: 0;
	}

	&.alert-enter-active {
		opacity: 1;
		right: 2rem;
		transition: all 50ms;
	}

	&.alert-enter-done {
		opacity: 1;
		right: 2rem;
	}

	&.alert-exit {
		opacity: 1;
		right: 2rem;
	}

	&.alert-exit-active {
		opacity: 0;
		right: 0;
		transition: all 50ms;
	}

	&.alert-exit-done {
		opacity: 0;
		right: 0;
	}

	/* caret up  */
	&:before {
		content: "";
		position: absolute;
		left: 0;
		top: 0;
		width: 5px;
		height: 100%;
		background: ${(p) =>
			p.type === "success"
				? p.theme.colorSuccess
				: p.type === "alert"
				? p.theme.colorActivation
				: p.theme.colorDanger};
	}
`;

const Alert = (props: AlertProps) => {
	const [showPopup, setShowPopup] = useState(props.show);
	const nodeRef = useRef(null);
	const theme = useTheme();

	const hidePopup = () => {
		setShowPopup(false);
		setTimeout(() => {
			props.hide();
		}, 150);
	};

	useEffect(() => {
		setShowPopup(props.show);
	}, [props.show]);

	// hide popup automatically after 2.5 seconds
	useEffect(() => {
		if (showPopup) {
			setTimeout(() => {
				hidePopup();
			}, 2500);
		}
	}, [showPopup]);

	return (
		<CSSTransition
			classNames="alert"
			in={showPopup}
			timeout={150}
			nodeRef={nodeRef}
			unmountOnExit
		>
			<StyledInfoPopup ref={nodeRef} type={props.type}>
				{props.children && props.children}
				<Icon
					icon="close"
					width="20px"
					color={theme.colorCopyLight}
					onClick={() => hidePopup()}
				/>
			</StyledInfoPopup>
		</CSSTransition>
	);
};

export default Alert;

interface AlertProps {
	show: boolean;
	hide(): void;
	children?: React.ReactNode;
	type: "error" | "success" | "alert";
}

export interface AlertMessage {
	type: "error" | "success" | "alert";
	message: string;
}
